import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';
import { useModal } from '../components/useModal';

const configReader = require('../../config');

/**
 * Transaction detail — one movement of money.
 *
 * All eleven fields of the old page and its single action, Cancel, which the old
 * page guarded with "Are you sure you want to proceed?". That sentence names
 * neither the transaction nor what cancelling does to it, so the dialog here says
 * both.
 *
 * The external references link into Adyen. Both rows point at the *same* psp
 * reference the old page computes — the original one when this is a refund —
 * because that is the payment Adyen shows under `txType=Payment`; a refund's own
 * reference does not resolve there. Kept as it was rather than "corrected" into
 * two separate links that would 404 half the time.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const price = value => {
  if (value === null || value === undefined) return '—';
  // `Price` is an object on some payloads and a number on others.
  if (typeof value === 'object') {
    const amount = value.Amount ?? value.amount;
    const currency = value.Currency ?? value.currency ?? 'DKK';
    return amount === null || amount === undefined ? '—' : `${Number(amount).toFixed(2)} ${currency}`;
  }
  return `${Number(value).toFixed(2)} DKK`;
};

const TransactionInternal = ({ api }) => {
  const { transactionId } = useParams();
  const { ask, modalProps } = useModal();

  const [transaction, setTransaction] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.transactions.getTransaction(transactionId);
      if (!data || data.success === false) {
        setFailed(true);
        return;
      }
      setTransaction(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, transactionId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/transactions">← All transactions</BackToList>
        <div className="next-note next-note--error">
          Could not load this transaction.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!transaction) return <div className="next-page next-muted">Loading…</div>;

  const { adyen } = configReader.get();
  const psp = transaction.Status === 'Refund'
    ? transaction.ExternalReferenceOriginal
    : transaction.ExternalReference;
  const adyenLink = psp
    ? `https://${adyen.domain}/ca/ca/accounts/showTx.shtml?txType=Payment&pspReference=${psp}`
    : null;

  const cancel = () => ask({
    kind: 'danger',
    title: 'Cancel this transaction?',
    context: `Cancel · transaction …${String(transactionId).slice(-6)}`,
    body: 'This refunds the whole purchase the transaction belongs to, not only this '
      + `${price(transaction.Price)}: every payment on it goes back to the payment provider, and the `
      + 'purchase and its products are marked Refunded.',
    warning: 'Refused while a signup of the purchase is still booked: use the signup’s force cancellation '
      + 'instead, which cancels and refunds. This admin cannot undo a refund.',
    confirmLabel: 'Cancel transaction',
    onConfirm: async () => {
      try {
        await api.transactions.cancelTransaction(transactionId);
        setNote({ tone: 'info', text: 'Cancellation sent.' });
      } catch (error) {
        setNote({ tone: 'error', text: `Unable to cancel: ${error?.message || 'unknown error'}` });
        await load();
        throw error;
      }
      await load();
    }
  });

  const facts = [
    ['Price', price(transaction.Price)],
    ['Status', transaction.Status
      ? <StatusPill status={transaction.Status}>{transaction.Status}</StatusPill>
      : <span className="next-muted">—</span>],
    ['Created at (CET)', fmtDate(transaction.CreatedAtCet)],
    ['Settle at (CET)', fmtDate(transaction.SettleAtCet)],
    ['Paid with', transaction.PaymentMethod?.Name || <span className="next-muted">—</span>],
    ['Payment method', transaction.PaymentMethodId
      ? <Guid value={transaction.PaymentMethodId} href={`/paymentmethod/creditcard/${transaction.PaymentMethodId}`} />
      : (transaction.PaymentMethod?.DisplayName || <span className="next-muted">—</span>)],
    ['Purchase id', transaction.PurchaseId
      ? <Guid value={transaction.PurchaseId} to={`/next/purchase/${transaction.PurchaseId}`} />
      : <span className="next-muted">—</span>],
    ['Subscription id', transaction.SubscriptionId
      ? <Guid value={transaction.SubscriptionId} to={`/next/subscription/${transaction.SubscriptionId}`} />
      : <span className="next-muted">—</span>],
    ['External reference', transaction.ExternalReference
      ? (adyenLink
        ? <a className="next-link" href={adyenLink} target="_blank" rel="noopener noreferrer">{transaction.ExternalReference}</a>
        : transaction.ExternalReference)
      : <span className="next-muted">—</span>],
    ['External reference original', transaction.ExternalReferenceOriginal
      ? (adyenLink
        ? <a className="next-link" href={adyenLink} target="_blank" rel="noopener noreferrer">{transaction.ExternalReferenceOriginal}</a>
        : transaction.ExternalReferenceOriginal)
      : <span className="next-muted">—</span>],
    ['Error text', transaction.ErrorText || <span className="next-muted">None</span>]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/transactions">← All transactions</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{price(transaction.Price)}</h1>
          {transaction.Status && <StatusPill status={transaction.Status}>{transaction.Status}</StatusPill>}
        </div>
        <span className="next-detail__id">{transaction.Id || transactionId}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Transaction</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            {/* Subscription charges have no purchase and the backend refuses to
                cancel them: those go through the subscription's own actions. */}
            {transaction.PurchaseId ? (
              <div className="next-actionlist">
                <button
                  type="button"
                  className="next-actionlist__item next-actionlist__item--danger"
                  onClick={cancel}
                >
                  Cancel transaction
                </button>
              </div>
            ) : (
              <p className="next-muted">
                A subscription charge: refund it from the subscription (regret or force termination).
              </p>
            )}
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                transaction.PurchaseId && {
                  label: 'Purchase',
                  hint: 'what was bought',
                  to: `/next/purchase/${transaction.PurchaseId}`
                },
                transaction.SubscriptionId && {
                  label: 'Subscription',
                  to: `/next/subscription/${transaction.SubscriptionId}`
                },
                transaction.PurchaseId && {
                  label: 'Other transactions',
                  hint: 'on this purchase',
                  to: `/next/transactions?purchaseId=${transaction.PurchaseId}`
                },
                transaction.SubscriptionId && {
                  label: 'Transactions',
                  hint: 'on this subscription',
                  to: `/next/transactions?subscriptionId=${transaction.SubscriptionId}`
                },
                transaction.PaymentMethodId && {
                  label: 'Payment method',
                  href: `/paymentmethod/creditcard/${transaction.PaymentMethodId}`
                },
                adyenLink && {
                  label: 'Open in Adyen',
                  hint: transaction.Status === 'Refund' ? 'the original payment' : undefined,
                  href: adyenLink
                }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

TransactionInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Transaction = withApi(TransactionInternal);

Transaction.requiresLogin = true;

export default Transaction;
